/* Scoped event orchestration and single-flight state, independent of transport/DOM. */
(() => {
  'use strict';
  const create = ({ page, api, view, emit = (cart, metadata) => {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'cart-page' } }));
    if (metadata) window.dataLayer?.push({ event: 'cart_framing_quantity_changed', ...metadata });
  } }) => {
    let busy = false;
    let blocked = false;
    let pendingRefresh = false;
    const renderState = () => view.state({ busy, blocked });
    const accept = (result, metadata) => { view.apply(result); blocked = false; emit(result.cart, metadata); };
    const recover = async () => { const result = await api.read(); accept(result); };
    const finish = () => {
      busy = false;
      renderState();
      if (pendingRefresh) { pendingRefresh = false; void refresh(); }
    };
    const refresh = async () => {
      if (busy) { pendingRefresh = true; return; }
      busy = true;
      renderState();
      view.notify('Refreshing your cart…');
      try { await recover(); view.notify('Your cart is up to date.'); }
      catch { blocked = true; view.notify('We could not refresh your cart. Please check your connection, then refresh your cart before continuing.', true); }
      finally { finish(); }
    };
    const change = async (key, quantity) => {
      if (busy || blocked) return;
      if (!Number.isSafeInteger(quantity) || quantity < 0) { view.notify('Please enter a whole number of items, zero or more.'); return; }
      busy = true;
      renderState();
      view.notify('Updating your cart…');
      let result;
      try {
        result = await api.update(key, quantity);
        accept(result, result.metadata);
        view.notify('Your cart has been updated.');
      } catch (error) {
        try {
          await recover();
          if (result?.metadata) window.dataLayer?.push({ event: 'cart_framing_quantity_changed', ...result.metadata });
          if (error.code === 'framing-split') view.notify('Please contact us to adjust this framed print combination. No quantity change was submitted.');
          else if (error.submitted) view.notify('Your latest cart is shown. Please check the quantities before making another change.');
          else if (result) view.notify('Your cart has been updated.');
          else view.notify('Your cart has been refreshed. Please check the quantities before making another change.');
        } catch {
          blocked = true;
          view.notify('We could not confirm your latest cart. Please refresh your cart before changing quantities or checking out.', true);
        }
      } finally { finish(); }
    };
    const click = (event) => {
      const refreshButton = event.target.closest?.('[data-cart-page-reconcile]');
      if (refreshButton && page.contains(refreshButton)) { event.preventDefault(); void refresh(); return; }
      const control = event.target.closest?.('[data-cart-page-change]');
      if (!control || !page.contains(control)) return;
      event.preventDefault();
      const row = control.closest('[data-cart-page-line]');
      const input = row?.querySelector('[data-cart-page-quantity]');
      const delta = Number(control.dataset.cartPageChange);
      if (!row || !input || ![0, -1, 1].includes(delta)) return;
      const current = input.value.trim() === '' ? NaN : Number(input.value);
      const valid = Number.isSafeInteger(current) && current >= 0;
      void change(row.dataset.cartPageLine, delta === 0 ? 0 : valid ? Math.max(0, current + delta) : NaN);
    };
    const inputChange = (event) => {
      if (!event.target.matches?.('[data-cart-page-quantity]')) return;
      const row = event.target.closest('[data-cart-page-line]');
      if (row) void change(row.dataset.cartPageLine, event.target.value.trim() === '' ? NaN : Number(event.target.value));
    };
    const submit = (event) => {
      if (event.target.matches?.('.cp-cart__form') && (busy || blocked)) {
        event.preventDefault();
        view.notify(busy ? 'Please wait while your cart updates.' : 'Please refresh your cart before checking out.', blocked);
      }
    };
    page.addEventListener('click', click);
    page.addEventListener('change', inputChange);
    page.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches?.('[data-cart-page-quantity]')) {
        event.preventDefault();
        inputChange(event);
      }
    });
    page.addEventListener('submit', submit);
    page.ownerDocument.addEventListener('cart:updated', (event) => {
      if (event.detail?.cart && event.detail.source !== 'cart-page') void refresh();
    });
    renderState();
    return Object.freeze({ change, refresh });
  };
  window.PFCartPageController = Object.freeze({ create });
})();
